import { FirestoreInstance } from './types/firestore';

enum DocumentType {
  Add = 'add',
  Update = 'update',
  Delete = 'delete',
}

type BatchedDocument<TDocRef> = {
  type: DocumentType;
  ref: TDocRef;
  data?: Record<string, unknown>;
};

export type BatchAdapter<TDocRef> = {
  set: (ref: TDocRef, data: Record<string, unknown>) => void;
  update: (ref: TDocRef, data: Record<string, unknown>) => void;
  delete: (ref: TDocRef) => void;
  commit: () => Promise<void>;
};

export type WriteBatchFn<TDocRef, TDb> = (db: TDb) => BatchAdapter<TDocRef>;

const FIRESTORE_BATCH_SIZE = 500;

export class BatchDocuments<TDocRef = unknown, TDb = FirestoreInstance> {
  private documents: BatchedDocument<TDocRef>[] = [];

  constructor(
    private readonly db: TDb,
    private readonly writeBatch: WriteBatchFn<TDocRef, TDb>,
  ) {}

  addDocument = ({
    ref,
    data,
  }: {
    ref: TDocRef;
    data: Record<string, unknown>;
  }) => {
    this.documents.push({ type: DocumentType.Add, ref, data });
  };

  updateDocument = ({
    ref,
    data,
  }: {
    ref: TDocRef;
    data: Record<string, unknown>;
  }) => {
    this.documents.push({ type: DocumentType.Update, ref, data });
  };

  deleteDocument = (ref: TDocRef) => {
    this.documents.push({ type: DocumentType.Delete, ref });
  };

  commit = async (): Promise<void> => {
    if (!this.documents.length) {
      return;
    }
    const batchCount = Math.ceil(this.documents.length / FIRESTORE_BATCH_SIZE);
    const batches = Array.from({ length: batchCount }, () =>
      this.writeBatch(this.db),
    );
    this.documents.forEach((document, index) => {
      const batch = batches[Math.floor(index / FIRESTORE_BATCH_SIZE)];
      if (document.type === DocumentType.Add) {
        batch.set(document.ref, document.data ?? {});
        return;
      }
      if (document.type === DocumentType.Update) {
        batch.update(document.ref, document.data ?? {});
        return;
      }
      batch.delete(document.ref);
    });
    await Promise.all(batches.map((batch) => batch.commit()));
    this.documents = [];
  };
}
